// no side effects allowed due to re-export by index.ts

import {
  attributesModule,
  classModule,
  eventListenersModule,
  init,
  propsModule,
  styleModule,
} from 'snabbdom';

import { isTouchDevice } from '@/device';
import { Janitor } from '@/event';
import { frag } from '@/index';
import { licon } from '@/licon';
import { pubsub } from '@/pubsub';
import * as xhr from '@/xhr';

import { focusableWithin } from './focus';
import { onInsert, hl, type VNode, type Attrs, type LooseVNodes } from './snabbdom';

export interface Dialog<Ctx = undefined> {
  ctx: Ctx;
  readonly view: HTMLElement; // your content div
  readonly dialog: HTMLDialogElement; // the dialog element
  readonly returnValue?: 'ok' | 'cancel' | string; // how did we close?

  show(): Promise<Dialog<Ctx>>; // promise resolves on close
  updateActions(actions?: Action<Ctx> | Action<Ctx>[]): void; // set actions, or reattach existing ones if omitted
  close(returnValue?: string): void;
}

type CssAsset = { url: string } | { hashed: string };

export interface DialogOpts<Ctx = undefined> {
  ctx?: Ctx;
  class?: string; // classes for your view div
  css?: CssAsset[]; // hashed or full url css
  attrs?: { dialog?: Attrs; view?: Attrs }; // optional attrs for dialog and view div
  focus?: string; // query selector for focus on show
  actions?: Action<Ctx> | Action<Ctx>[]; // add listeners to controls, call updateActions() to reattach
  onShow?: (dialog: Dialog<Ctx>) => void; // called after dialog is shown
  onClose?: (dialog: Dialog<Ctx>) => void; // always called when dialog closes
  noCloseButton?: boolean; // if true, no upper right corner close button
  noScrollable?: boolean; // if true, no scrollable div container. Fixes dialogs containing an auto-completer
  noTabCycle?: boolean; // if true, dont trap tab focus within the dialog
  modal?: boolean; // if true, show as modal (darken everything else)
  easyClose?: 'anyClick' | 'clickOutside';
}

// show is an explicit property for domDialog.
export interface DomDialogOpts<Ctx = undefined> extends DialogOpts<Ctx> {
  htmlText?: string; // content, htmlText is inserted as fragment into DOM
  cash?: Cash; // content, precedence over htmlText, cash will be cloned and any 'none' class removed
  htmlUrl?: string; // content, precedence over htmlText and cash, url will be xhr'd
  insert?: { nodes: Node | Node[]; selector?: string; position?: 'child' | 'before' | 'after' }[]; // 'child'
  parentEl?: Element; // parent for placement and centering; defaults to document.body
  show?: boolean; // show dialog immediately after construction
}

export interface SnabDialogOpts<Ctx = undefined> extends DialogOpts<Ctx> {
  htmlUrl?: string; // legacy HTML content; ignored when vnodes are provided
  vnodes?: LooseVNodes; // dialog content
  onInsert?: (dialog: Dialog<Ctx>) => void; // if provided you must call show
}

// unlike snabDialog, jsxDialog patches a standalone root. it does not slide into an existing vdom tree.
// call it once with a render callback: jsxDialog({ ..., render: (redraw, dlg) => [<div>...</div>, ...] });
export interface JsxDialogOpts<Ctx = undefined> extends DialogOpts<Ctx> {
  render: (redraw: Redraw, dialog: Dialog<Ctx>) => LooseVNodes;
  parentEl?: Element; // unmanaged container for the vdom root. defaults to document.body
}

export type ActionListener<T extends Event = Event, Ctx = undefined> = (
  e: T,
  dialog: Dialog<Ctx>,
  action: Action<Ctx>,
) => void;

// Actions are listeners / results for controls
// if no event is specified, then 'click' is assumed
// if no selector is given, the handler is attached to the dialog-content view div
export type Action<Ctx = undefined> =
  | { selector?: string; event?: string | string[]; listener: ActionListener<any, Ctx> }
  | { selector?: string; event?: string | string[]; result: string };

// when opts contains 'show', domDialog resolves when the dialog is closed
// otherwise, domDialog resolves when assets are loaded and is ready to show
export async function domDialog<Ctx = undefined>(o: DomDialogOpts<Ctx>): Promise<Dialog<Ctx>> {
  const html = await loadWithCss(
    o.css,
    o.htmlUrl
      ? xhr.text(o.htmlUrl)
      : Promise.resolve(o.cash?.clone().removeClass('none')[0]?.outerHTML ?? o.htmlText),
  );

  const dialog = document.createElement('dialog');
  for (const [k, v] of Object.entries(o.attrs?.dialog ?? {})) dialog.setAttribute(k, String(v));
  if (isTouchDevice()) dialog.classList.add('touch-scroll');
  if (o.parentEl) dialog.style.position = 'absolute';

  if (!o.noCloseButton) {
    const anchor = frag<Element>('<div class="close-button-anchor">');
    anchor.innerHTML = `<button class="close-button" aria-label="Close" data-icon="${licon.X}">`;
    dialog.appendChild(anchor);
  }

  const view = document.createElement('div');
  view.classList.add('dialog-content');
  if (o.class) view.classList.add(...o.class.split(/[. ]/).filter(Boolean));
  for (const [k, v] of Object.entries(o.attrs?.view ?? {})) view.setAttribute(k, String(v));
  if (html) view.innerHTML = html;

  const scrollable = frag<Element>(`<div class="${o.noScrollable ? 'not-' : ''}scrollable">`);
  scrollable.appendChild(view);
  dialog.appendChild(scrollable);

  (o.parentEl ?? document.body).appendChild(dialog);

  for (const app of o.insert ?? []) {
    if (app.nodes === view) break;
    const nodes = Array.isArray(app.nodes) ? app.nodes : [app.nodes];
    const target = (app.selector ? view.querySelector(app.selector) : view)!;
    if (app.position === 'before') target.before(...nodes);
    else if (app.position === 'after') target.after(...nodes);
    else target.append(...nodes);
  }

  const wrapper = new DialogWrapper<Ctx>(dialog, view, o);
  return o.show ? wrapper.show() : wrapper;
}

export function snabDialog<Ctx = undefined>(o: SnabDialogOpts<Ctx>): VNode {
  let dialogEl: HTMLDialogElement;
  const classes = o.class?.split(/[. ]/).filter(Boolean) ?? [];
  const dialogVNode = hl(
    'dialog',
    {
      class: { 'touch-scroll': isTouchDevice() },
      key: o.class ?? 'dialog',
      attrs: o.attrs?.dialog,
      hook: onInsert(el => (dialogEl = el as HTMLDialogElement)),
    },
    [
      o.noCloseButton ||
        hl(
          'div.close-button-anchor',
          hl('button.close-button', { attrs: { 'data-icon': licon.X, 'aria-label': i18n.site.close } }),
        ),
      hl(
        'div',
        { class: { scrollable: !o.noScrollable } },
        hl(
          'div.dialog-content',
          {
            class: Object.fromEntries(classes.map(c => [c, true])),
            attrs: o.attrs?.view,
            hook: {
              ...onInsert(async view => {
                const html = await loadWithCss(o.css, o.htmlUrl ? xhr.text(o.htmlUrl) : undefined);
                if (!o.vnodes && html) view.innerHTML = html;
                const dlg = new DialogWrapper<Ctx>(dialogEl, view, o);
                if (o.onInsert) o.onInsert(dlg);
                else dlg.show();
              }),
            },
          },
          o.vnodes,
        ),
      ),
    ],
  );
  if (!o.modal) return dialogVNode;
  return hl('div.snab-modal-mask', { class: { none: Boolean(o.onInsert) } }, dialogVNode);
}

export function jsxDialog<Ctx = undefined>(o: JsxDialogOpts<Ctx>): Redraw {
  const patch = init([classModule, attributesModule, propsModule, eventListenersModule, styleModule]);
  const { render, parentEl, ...dialogOpts } = o;

  let vnode: VNode | Element = (parentEl ?? document.body).appendChild(document.createElement('div'));
  let dialog: Dialog<Ctx> | undefined;

  const redraw = () => {
    if (!dialog) return;
    vnode = patch(vnode, snabDialog({ ...dialogOpts, vnodes: render(redraw, dialog) }));
  };
  vnode = patch(
    vnode,
    snabDialog({
      ...dialogOpts,
      onInsert: dlg => {
        dialog = dlg;
        if (parentEl) dialog.dialog.style.position = 'absolute';
        redraw();
        dialog.show();
      },
    }),
  );
  return redraw;
}

const easyCloseHandler = new (class {
  private stack: DialogWrapper[] = [];

  push(dlg: DialogWrapper<any>) {
    if (!dlg.o.easyClose) return;
    if (this.stack.length === 0)
      document.addEventListener('pointerdown', this.pointerdown, { capture: true });
    this.stack.push(dlg);
  }

  remove(dlg: DialogWrapper<any>): void {
    this.stack = this.stack.filter(d => d !== dlg);
    if (this.stack.length === 0)
      document.removeEventListener('pointerdown', this.pointerdown, { capture: true });
  }

  private readonly pointerdown = (e: PointerEvent) => {
    if (!this.top?.o.easyClose) return;

    if (this.top.o.easyClose === 'clickOutside') {
      const { clientX: x, clientY: y } = e;
      const bounds = this.top.dialog.getBoundingClientRect();
      if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) return;
    }
    if (e.pointerType === 'touch' || !this.top.o.modal)
      window.addEventListener(
        'click',
        e => {
          e.preventDefault();
          e.stopImmediatePropagation();
        },
        { once: true, capture: true },
      );
    this.top.close('cancel');
    e.stopPropagation();
  };

  private get top(): DialogWrapper | undefined {
    return this.stack[this.stack.length - 1];
  }
})();

class DialogWrapper<Ctx = undefined> implements Dialog<Ctx> {
  private readonly dialogEvents = new Janitor();
  private readonly actionEvents = new Janitor();
  private resolve?: (dialog: Dialog<Ctx>) => void;
  private readonly observer: MutationObserver = new MutationObserver(list => {
    for (const m of list)
      if (m.type === 'childList')
        for (const n of m.removedNodes) {
          if (n === this.dialog) {
            this.onRemove();
            return;
          }
        }
  });

  constructor(
    readonly dialog: HTMLDialogElement,
    readonly view: HTMLElement,
    readonly o: DialogOpts<Ctx>,
    public ctx: Ctx = o.ctx as Ctx,
  ) {
    this.observer.observe(document.body, { childList: true, subtree: true });
    document.body.style.setProperty('---viewport-height', `${window.innerHeight}px`);
    this.dialogEvents.addListener(view, 'click', e => e.stopPropagation());
    this.dialogEvents.addListener(dialog, 'cancel', e => {
      if (!o.easyClose && o.noCloseButton && o.class !== 'alert') return e.preventDefault();
      if (!this.dialog.returnValue) this.dialog.returnValue = 'cancel';
    });
    this.dialogEvents.addListener(dialog, 'close', this.onRemove);
    if (!o.noCloseButton)
      this.dialogEvents.addListener(
        dialog.querySelector<HTMLButtonElement>('.close-button-anchor > .close-button')!,
        'click',
        () => this.close('cancel'),
      );
    this.updateActions();
    this.dialogEvents.addListener(this.dialog, 'keydown', this.onKeydown);
  }

  get returnValue(): string {
    return this.dialog.returnValue;
  }

  show = async (): Promise<Dialog<Ctx>> => {
    (await pubsub.after('polyfill.dialog'))?.(this.dialog);
    const snabModal = this.dialog.parentElement === this.dialog.closest('.snab-modal-mask');
    if (this.o.modal) this.view.scrollTop = 0;
    if (snabModal) this.dialog.parentElement?.classList.remove('none');

    if (this.o.modal && !snabModal) this.dialog.showModal();
    else this.dialog.show();

    easyCloseHandler.push(this);
    this.dialogEvents.addCleanupTask(() => easyCloseHandler.remove(this));

    this.autoFocus();
    this.o.onShow?.(this);
    return new Promise(resolve => (this.resolve = resolve));
  };

  close = (v?: string) => {
    this.dialog.close(v || this.returnValue || 'ok');
  };

  updateActions = (actions = this.o.actions) => {
    this.actionEvents.cleanup();
    this.o.actions = actions;
    if (!actions) return;
    for (const a of Array.isArray(actions) ? actions : [actions]) {
      for (const event of Array.isArray(a.event) ? a.event : a.event ? [a.event] : ['click']) {
        for (const el of a.selector ? this.view.querySelectorAll(a.selector) : [this.view]) {
          const listener =
            'listener' in a ? (e: Event) => a.listener(e, this, a) : () => this.close(a.result);
          this.actionEvents.addListener(el, event, listener);
        }
      }
    }
  };

  private readonly onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && (this.o.easyClose || !this.o.noCloseButton)) {
      this.close('cancel');
      e.preventDefault();
    } else if (e.key === 'Tab' && !this.o.noTabCycle) {
      const focii = focusableWithin(this.dialog);
      focii.sort((a, b) => {
        const ati = Number(a.getAttribute('tabindex') ?? '0');
        const bti = Number(b.getAttribute('tabindex') ?? '0');
        if (ati > 0 && (bti === 0 || ati < bti)) return -1;
        else if (bti > 0 && ati !== bti) return 1;
        else return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1;
      });
      const first = focii[0];
      const last = focii[focii.length - 1];
      const focus = document.activeElement as HTMLElement;

      if (focus === last && !e.shiftKey) first?.focus();
      else if (focus === first && e.shiftKey) last?.focus();
      else return;
      e.preventDefault();
    }

    if (e.key === 'Escape' || (e.key === 'Tab' && !this.o.noTabCycle)) e.stopPropagation();
  };

  private autoFocus() {
    const focus =
      (this.o.focus ? this.view.querySelector(this.o.focus) : this.view.querySelector('input[autofocus]')) ??
      focusableWithin(this.view)[0];

    if (!(focus instanceof HTMLElement)) return;
    focus.focus();
    if (focus instanceof HTMLInputElement) focus.select();
  }

  private readonly onRemove = () => {
    this.observer.disconnect();
    if (!this.dialog.returnValue) this.dialog.returnValue = 'cancel';
    this.resolve?.(this);
    this.o.onClose?.(this);
    if (this.dialog.parentElement?.classList.contains('snab-modal-mask')) {
      this.dialog.parentElement.remove(); // TODO - this is bad if snab-modal-mask's parent is vdom
    } else {
      this.dialog.remove();
    }
    for (const css of this.o.css ?? []) {
      if ('hashed' in css) site.asset.removeCssPath(css.hashed);
      else if ('url' in css) site.asset.removeCss(css.url);
    }
    this.actionEvents.cleanup();
    this.dialogEvents.cleanup();
  };
}

async function loadWithCss<T = string>(css: CssAsset[] = [], loadMe?: Promise<T>): Promise<T | undefined> {
  const results = await Promise.allSettled([
    loadMe,
    site.asset.loadCssPath('bits.dialog'),
    ...css.map(asset =>
      'hashed' in asset ? site.asset.loadCssPath(asset.hashed) : site.asset.loadCss(asset.url),
    ),
  ]);
  return results[0]?.status === 'fulfilled' ? results[0].value : undefined;
}
